import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Input,
  List,
  ListItem,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Typography,
} from '@mui/joy';
import { Search } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { shiftTypeService } from '@/services/shiftTypeService';
import { userService } from '@/services/userService';
import { ShiftType } from '@/types/shiftType';
import { User } from '@/types/user';

interface AssignShiftTypeEmployeesModalProps {
  open: boolean;
  shiftType: ShiftType | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function AssignShiftTypeEmployeesModal({
  open,
  shiftType,
  onClose,
  onSaved,
}: AssignShiftTypeEmployeesModalProps) {
  const [employees, setEmployees] = useState<User[]>([]);
  const [initialSelection, setInitialSelection] = useState<Set<number>>(new Set());
  const [selection, setSelection] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open || !shiftType) return;

    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const [allEmployees, assignedEmployees] = await Promise.all([
          userService.getEmployees(),
          shiftTypeService.getEmployeesForShiftType(shiftType.id),
        ]);
        if (cancelled) return;
        const assignedIds = new Set(assignedEmployees.map((e) => e.id));
        setEmployees(allEmployees.filter((u) => u.is_active));
        setInitialSelection(assignedIds);
        setSelection(new Set(assignedIds));
      } catch (error: any) {
        toast.error(error?.response?.data?.error || 'Failed to load employees');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, shiftType]);

  const filteredEmployees = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return employees;
    return employees.filter((u) => {
      const full = `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase();
      return full.includes(term);
    });
  }, [employees, search]);

  const toggle = (id: number) => {
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    if (!shiftType) return;

    const toAdd: number[] = [];
    const toRemove: number[] = [];
    selection.forEach((id) => {
      if (!initialSelection.has(id)) toAdd.push(id);
    });
    initialSelection.forEach((id) => {
      if (!selection.has(id)) toRemove.push(id);
    });

    if (toAdd.length === 0 && toRemove.length === 0) {
      onClose();
      return;
    }

    setIsSaving(true);
    try {
      if (toAdd.length) await shiftTypeService.assignShiftTypeToEmployees(shiftType.id, toAdd);
      if (toRemove.length) await shiftTypeService.removeShiftTypeFromEmployees(shiftType.id, toRemove);
      toast.success('Assignments updated');
      onSaved();
      onClose();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Failed to update assignments');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={isSaving ? undefined : onClose}>
      <ModalDialog size="md" sx={{ width: { xs: '95vw', sm: 520 } }}>
        <ModalClose />
        <Typography level="h4">Assign Employees</Typography>
        {shiftType && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: shiftType.color,
              }}
            />
            <Typography level="body-sm" color="neutral">
              {shiftType.name}
            </Typography>
            <Chip size="sm" variant="soft">
              {selection.size} selected
            </Chip>
          </Stack>
        )}

        <Input
          placeholder="Search employees..."
          startDecorator={<Search />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 1 }}
        />

        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 'sm',
            maxHeight: 360,
            minHeight: 200,
            overflowY: 'auto',
          }}
        >
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
              <CircularProgress size="sm" />
            </Box>
          ) : filteredEmployees.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography level="body-sm" color="neutral">
                {search ? 'No employees match your search' : 'No employees found'}
              </Typography>
            </Box>
          ) : (
            <List sx={{ '--ListItem-paddingY': '6px' }}>
              {filteredEmployees.map((employee) => {
                const checked = selection.has(employee.id);
                return (
                  <ListItem
                    key={employee.id}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => toggle(employee.id)}
                  >
                    <Checkbox
                      checked={checked}
                      onChange={() => toggle(employee.id)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ mr: 1.5 }}
                    />
                    <Stack sx={{ flex: 1, minWidth: 0 }}>
                      <Typography level="body-sm" noWrap>
                        {employee.first_name} {employee.last_name}
                      </Typography>
                      <Typography level="body-xs" color="neutral" noWrap>
                        {employee.email}
                        {employee.role !== 'employee' ? ` · ${employee.role}` : ''}
                      </Typography>
                    </Stack>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button variant="plain" color="neutral" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={isSaving}>
            Save
          </Button>
        </Stack>
      </ModalDialog>
    </Modal>
  );
}
